import {workspaceTabIsVisibleKey} from '@/keys';
import {Pane, paneKey, stateKey, Tab, TabView, Workspace} from '@/view-managers/workspace/workspace-types';
import {useDialogs} from '@hantera/portal-app';
import {getCurrentInstance, inject, nextTick, onMounted, ref, watch} from 'vue';
import {v7} from 'uuid';

/**
 * @deprecated Use PortalContext (useContext()) instead
 */
export default function (state?: Workspace) {

  const dialogs = useDialogs();
  const instance = getCurrentInstance();
  if (!state) {
    state = inject(stateKey, {panes: [], activePaneId: undefined, showSidebar: true, sidebarWidth: 230});
  }
  const workspaceTabIsVisible = inject(workspaceTabIsVisibleKey, ref(true));
  let onVisibleCallback: (() => void) | undefined;

  watch(workspaceTabIsVisible, val => {
    if (val && onVisibleCallback) {
      nextTick(() => {
        onVisibleCallback?.();
      });
    }
  });
  onMounted(() => {
    if (workspaceTabIsVisible.value && onVisibleCallback) {
      onVisibleCallback();
    }
  });

  /**
   * Opens a new tab. If a tab with the same tabId already exists, selects that tab in it's pane.
   * @param paneId 
   * @param tab 
   * @param replaceChildOfTabId 
   * @returns the new, existing or replaced tab
   */
  function openTab(paneId: string, tab: Tab, replaceChildOfTabId?: string) {
    const pane = state!.panes.find(r => r.paneId === paneId) || state!.panes[0];
    
    if (replaceChildOfTabId) {
      const children = getChildrenOf(replaceChildOfTabId);
      if (children.length > 0) {
        Object.assign(children[0], tab);
        
        const existingPane = getPaneFor(children[0].tabId);
        if (existingPane) {
          existingPane.selectedTabId = tab.tabId;
        }

        return children[0];
      }
    }
    
    const existing = getTab(tab.tabId);
    if (existing) {
      const existingPane = getPaneFor(existing.tabId);
      if (existingPane) {
        existingPane.selectedTabId = 'new';
      }
      return existing;
    }
    
    pane.tabs.push(tab);
    pane.selectedTabId = tab.tabId;
  
    return tab;
  }

  function selectLastOrNewTab(pane: Pane) {
    pane.selectedTabId = pane.tabs.length > 0 ? pane.tabs[pane.tabs.length - 1].tabId : 'new'; // 'new' will select the "new tab" viewer
  }

  function getPane(paneId: string) {
    return state!.panes.find(r => r.paneId === paneId);
  }

  async function closeTab(tabId: string) {
    let tab = getTab(tabId);

    if (tab?.hasUnsavedChanges && !await dialogs.confirm('You have unsaved changes. Do you still want to close this tab?')) {
      return;
    }

    tab = detachTab(tabId);
    if (tab) {
      getChildrenOf(tabId).forEach(child => detachTab(child.tabId));
    }
  }

  function getChildrenOf(tabId: string) {
    const result: Tab[] = [];
    state!.panes.forEach((pane, idx) => {
      result.push(...pane.tabs.filter(t => t.parentTabId === tabId));
    });
    return result;
  }

  function getPaneFor(tabId: string) {
    return state!.panes.find(r => r.tabs.findIndex(t => t.tabId === tabId) >= 0);
  }

  function getTab(tabId: string) {
    var pane = getPaneFor(tabId);
    if (!pane) { return; }

    const idx = pane?.tabs.findIndex(r => r.tabId === tabId);

    if (typeof idx === 'number' && idx >= 0) {
      return pane.tabs[idx];
    }
  }

  /**
   * Detaches a tab from it's current pane but doesn't remove child tabs
   * @param tabId 
   * @returns the tab that was detached
   */
  function detachTab(tabId: string) {
    const pane = getPaneFor(tabId);
    if (!pane) { return; }

    const idx = pane?.tabs.findIndex(r => r.tabId === tabId);
    if (typeof idx === 'number' && idx >= 0) {
      const tab = pane.tabs[idx];
      pane.tabs.splice(idx, 1);

      if (pane.selectedTabId === tabId) {
        if (pane.tabs.length > idx) {
          pane.selectedTabId = pane.tabs[idx].tabId;
        } else {
          selectLastOrNewTab(pane);
        }
      }
      return tab;
    }
    return undefined;
  }

  function moveTab(tabId: string, targetPos: number, targetPaneId?: string) {
    const sourcePane = getPaneFor(tabId);
    const targetPane = targetPaneId ? getPane(targetPaneId) : sourcePane;
    if (!sourcePane || !targetPane) { return; }

    const tab = detachTab(tabId);
    if (tab) {
      targetPane.tabs.splice(targetPos, 0, tab);
      targetPane.selectedTabId = tab.tabId;
    }
  }

  function getPositionOf(tabId: string) {
    const pane = getPaneFor(tabId);
    if (pane) {
      return pane.tabs.findIndex(r => r.tabId === tabId);
    }
    return -1;
  }

  function openViewTab(path: string, label: string) {
    let pane: Pane | undefined;

    if (state!.activePaneId) {
      pane = state!.panes.find(r => r.paneId === state!.activePaneId);
    }
    if (!pane) {
      pane = state!.panes[0];
    }

    if (!pane) { return false; }

    var existing = pane.tabs.find(r => (r as TabView).path === path);

    if (existing) {
      pane.selectedTabId = existing.tabId;

      return true;
    }

    openTab(pane.paneId, {
      type: 'view',
      tabId: v7(),
      path,
      state: {},
      label
    } as TabView);

    return true;
  }

  return {
    setLabel(label?: string) {
      instance?.emit('setLabel', label || '');
    },
    state,
    /**
     * Current parent pane
     */
    pane: inject(paneKey),
    getPane,
    openTab,
    closeTab,
    moveTab,
    getPaneFor,
    getPositionOf,
    getTab,
    getChildrenOf,
    /**
     * If the component is rendered in a workspace tab, this callback will be called
     * every time the tab is visible/selected. If the tab is visible at initial render,
     * then it will be called on mounted lifecycle hook.
     * @param callback 
     */
    onVisible(callback: () => void) {
      onVisibleCallback = callback;
    },
    openViewTab
  };
}